import { loadStripe } from "@stripe/stripe-js";
import { call, put, takeEvery } from "redux-saga/effects";
import {
  clearCheckoutResumeFromOnHold,
  createOrderAction,
  createOrderFailure,
  createOrderSuccess,
  getAddonsAction,
  getAddonsFailure,
  getAddonsSuccess,
  getOrdersAction,
  getOrdersFailure,
  getOrdersSuccess,
  getShippingRatesAction,
  getShippingRatesFailure,
  getShippingRatesSuccess,
} from "../slices/order";

import { CHECKOUT_RESUME_ORDER_ID_STORAGE_KEY } from "src/constants/checkoutResume";
import { CONFIG } from "src/global-config";
import { InitializeSafeChargeProps } from "src/hooks/useNuveiPayment";
import {
  createCheckoutSession,
  createOrder,
  getAddons,
  getOrders,
  getShippingRates,
} from "../apis/order";
import { clearCart } from "../slices/cart";

function* handleStripePayment(order: any): Generator {
  try {
    const stripe = yield call(
      loadStripe,
      CONFIG?.REACT_APP_STRIPE_PUBLISHABLE_KEY as string,
    );
    const baseUrl = window.location.origin;
    if (!stripe) {
      console.log("Stripe not loaded");
      return;
    }
    const body = {
      cartItems: order?.items?.map((item: any) => ({
        amount: item?.price,
        images: [item?.productId?.productGalleries[0]?.medium ?? ""],
        ...item,
      })),
      currency: "CAD",
      taxTotal: order?.taxTotal,
      successUrl: `${baseUrl}/success?orderId=${order?.orderId}`,
      cancelUrl: `${baseUrl}/cancel?orderId=${order?.orderId}`,
      customerEmail: order?.billingEmail,
      orderId: order?.orderId ?? "10082",
    };
    const session: any = yield call(createCheckoutSession, body);
    if (session) {
      const result: any = yield call(
        [(stripe as any)?.default, (stripe as any)?.redirectToCheckout],
        {
          sessionId: session?.id as string,
        },
      );
      if (result.error) {
        console.log(result.error, "error");
        return;
      }
    }
  } catch (error: any) {
    console.log(error, "payment failed error");
  }
}

function* handleNuveiPayment(
  order: any,
  initializeSafeCharge: (props: InitializeSafeChargeProps) => void,
): Generator {
  try {
    const baseUrl = window.location.origin;
    const nuveiTraceId = `nuvei_${order?.orderId ?? "unknown"}_${Date.now()}`;
    const body = {
      cartItems: order?.items?.map((item: any) => ({
        amount: item?.price,
        images: [item?.productId?.productGalleries[0]?.medium ?? ""],
        ...item,
      })),
      provider: "nuvei",
      currency: "CAD",
      taxTotal: order?.taxTotal,
      addresses: order?.addresses ?? [],
      customerEmail: order?.billingEmail,
      userTokenId: String(
        order?.customer?._id ?? order?.customer ?? order?.billingEmail ?? "",
      ),
      nuveiTraceId,
      orderId: order?.orderId ?? "",
      successUrl: `${baseUrl}/success?orderId=${order?.orderId}`,
      cancelUrl: `${baseUrl}/cancel?orderId=${order?.orderId}`,
      amount_total: order?.totalAmount ?? 0,
    };
    const session: any = yield call(createCheckoutSession, body);
    const nuveiDetails = {
      ...(session ?? {}),
      ...(session?.nuvei_order ?? {}),
      clientPaymentUrl:
        session?.clientPaymentUrl ?? session?.nuvei_order?.clientPaymentUrl,
      integrationMode:
        session?.integrationMode ??
        session?.nuvei_order?.integrationMode ??
        "websdk",
      simplyConnect: session?.simplyConnect ?? session?.nuvei_order?.simplyConnect,
      nuveiEnv: session?.nuveiEnv ?? session?.nuvei_order?.nuveiEnv,
      sessionToken: session?.sessionToken ?? session?.nuvei_order?.sessionToken,
      merchantId: session?.merchantId ?? session?.nuvei_order?.merchantId,
      merchantSiteId:
        session?.merchantSiteId ?? session?.nuvei_order?.merchantSiteId,
      userTokenId: session?.userTokenId ?? session?.nuvei_order?.userTokenId,
      customerEmail: body.customerEmail,
      currency: body.currency,
      nuveiTraceId: session?.nuveiTraceId ?? nuveiTraceId,
    };
    initializeSafeCharge({
      orderDetails: nuveiDetails,
      orderId: order?.orderId ?? "",
      customerName:
        order?.customer?.firstName + " " + order?.customer?.lastName || "",
      totalAmount: order?.totalAmount ?? 0,
    });
  } catch (error: any) {
    window.location.href = `/cancel?orderId=${order?.orderId}`;
  }
}

function* handleMakePayment(
  order: any,
  initializeSafeCharge: (props: InitializeSafeChargeProps) => void,
): Generator {
  try {
    switch (order?.paymentMethod) {
      case "stripe":
        yield call(handleStripePayment, order);
        break;
      case "nuvei":
        yield call(handleNuveiPayment, order, initializeSafeCharge);
        break;
      default:
        console.warn(`Unsupported payment method: ${order?.paymentMethod}`);
        break;
    }
  } catch (error: any) {
    console.log("Payment handling failed:", error);
  }
}

function* handleCreateOrder(action: { type: string; payload: any }): Generator {
  try {
    const order: any = yield call(createOrder, action.payload.checkoutData);
    if (order) {
      yield put(clearCheckoutResumeFromOnHold());
      try {
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.removeItem(CHECKOUT_RESUME_ORDER_ID_STORAGE_KEY);
        }
      } catch {
        /* ignore */
      }
      yield put(createOrderSuccess(order));
      const normalizedStatus = String(order?.status || "")
        .trim()
        .toLowerCase();
      const shouldClearCartNow = normalizedStatus === "processing";
      if (shouldClearCartNow) {
        yield put(clearCart());
      }
      if (["pay-on-delivery", "credit-point"].includes(order?.paymentMethod)) {
        action.payload.navigate(
          "/profile?activeTab=orders&orderId=" + order?.orderId,
        );
      } else {
        yield call(
          handleMakePayment,
          order,
          action.payload.initializeSafeCharge,
        );
      }
    }
  } catch (error: any) {
    yield put(createOrderFailure(error.message || "Failed to create order"));
  }
}

function* handleGetOrders(action: { type: string; payload: any }): Generator {
  try {
    const orders: any = yield call(getOrders);
    if (orders) {
      yield put(getOrdersSuccess(orders));
    }
  } catch (error: any) {
    yield put(getOrdersFailure(error.message || "Failed to get orders"));
  }
}

function* handleGetShippingRates(action: {
  type: string;
  payload: any;
}): Generator {
  try {
    const shippingRates: any = yield call(getShippingRates, action?.payload);
    if (shippingRates) {
      yield put(getShippingRatesSuccess(shippingRates));
    }
  } catch (error: any) {
    yield put(
      getShippingRatesFailure(error.message || "Failed to get shipping rates"),
    );
  }
}

function* handleGetAddons(): Generator {
  try {
    const addons: any = yield call(getAddons);
    if (addons) {
      yield put(getAddonsSuccess(addons));
    }
  } catch (error: any) {
    yield put(getAddonsFailure(error.message || "Failed to get addons"));
  }
}

export default function* orderSaga() {
  yield takeEvery(createOrderAction.type, handleCreateOrder);
  yield takeEvery(getOrdersAction.type, handleGetOrders);
  yield takeEvery(getShippingRatesAction.type, handleGetShippingRates);
  yield takeEvery(getAddonsAction.type, handleGetAddons);
}
